import { useState } from 'react';
import {
  ListItem,
  Stack,
  Text,
  UnorderedList,
  useToast,
} from '@chakra-ui/react';
import { ShieldCheck, Trash2, UserX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { ConfirmSheet } from './ConfirmSheet';
import { api } from '../lib/api';
import { confirmItsYou, deviceErrorMessage } from '../lib/devices';
import { useAuth } from '../hooks';
import { accessTokenAtom } from '../store';
import { isReviewSession, REVIEW_VERIFICATION } from '../lib/reviewSession';
import { useT } from '../i18n';

interface DeleteAccountSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

// Delete my account (M51, D172): a sheet that says what happens, then a
// fresh passkey check (D91), then a final Delete. No typed confirmation.
export function DeleteAccountSheet({
  isOpen,
  onClose,
}: DeleteAccountSheetProps) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { currentUser, signOutDeletedAccount } = useAuth();
  const accessToken = useAtomValue(accessTokenAtom);
  // The passkey check, once done; the final step sends it.
  const [verification, setVerification] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const close = () => {
    if (busy) return;
    setVerification(null);
    onClose();
  };

  const showError = (err: unknown) => {
    const message = deviceErrorMessage(err);
    if (message) toast({ title: message, status: 'error' });
  };

  const check = async () => {
    // An app reviewer's demo account has no passkey; its review link was
    // the proof (M56, D204).
    if (isReviewSession(accessToken)) {
      setVerification(REVIEW_VERIFICATION);
      return;
    }
    setBusy(true);
    try {
      setVerification(await confirmItsYou());
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.deleteMyAccount(verification);
    } catch (err) {
      // A check is used up either way; start again from it.
      setVerification(null);
      setBusy(false);
      showError(err);
      return;
    }
    await signOutDeletedAccount();
    toast({ title: t('account.delete.done'), status: 'info' });
    navigate('/', { replace: true });
  };

  if (verification) {
    return (
      <ConfirmSheet
        isOpen={isOpen}
        onClose={close}
        centered
        icon={<Trash2 size={28} />}
        title={t('account.delete.confirmTitle')}
        body={t('account.delete.confirmBody')}
        confirmLabel={t('account.delete.confirm')}
        confirmIcon={<Trash2 size={18} />}
        cancelLabel={t('account.delete.keep')}
        onConfirm={() => void remove()}
        isLoading={busy}
      />
    );
  }

  return (
    <ConfirmSheet
      isOpen={isOpen}
      onClose={close}
      icon={<UserX size={28} />}
      title={t('account.delete.title')}
      body={
        <Stack spacing={3} textAlign="left">
          <UnorderedList spacing={2} ml={5}>
            <ListItem>{t('account.delete.messages')}</ListItem>
            <ListItem>{t('account.delete.devices')}</ListItem>
            <ListItem>{t('account.delete.roots')}</ListItem>
            <ListItem>
              {t('account.delete.handle', {
                handle: currentUser?.handle ?? '',
              })}
            </ListItem>
          </UnorderedList>
          <Text fontWeight={700}>{t('account.delete.intro')}</Text>
        </Stack>
      }
      confirmLabel={t('account.delete.continue')}
      confirmIcon={<ShieldCheck size={18} />}
      cancelLabel={t('account.delete.keep')}
      onConfirm={() => void check()}
      isLoading={busy}
    />
  );
}
